/**
 * @class ProductGallery
 * @extends HTMLElement
 * @description A custom web component that creates a responsive product image gallery using Swiper.js.
 * The gallery automatically adapts to different screen sizes and breakpoints, with special handling for desktop views.
 *
 * Below the laptop breakpoint the gallery is a one-slide-per-view carousel. So shoppers can tell
 * there is more than one photo, the block renders a "1 / N" counter over the image
 * (`[data-gallery-counter]`) and a strip of tappable thumbnails under it (`[data-gallery-thumb]`);
 * this component keeps both in sync with the active slide. On laptop and up Swiper is destroyed,
 * the slides render as a grid and both helpers are hidden by CSS.
 * 
 * @example
 * // Usage in HTML/Liquid
 * <product-gallery>
 *   <div class="swiper product-gallery">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">
 *         <img src="product-image.jpg" alt="Product Image">
 *       </div>
 *     </div>
 *     <div class="swiper-pagination"></div>
 *   </div>
 * </product-gallery>
 */
class ProductGallery extends HTMLElement {
  /** @static {string[]} BREAKPOINT_MODES - Screen modes where Swiper should be disabled */
  static BREAKPOINT_MODES = ['laptop', 'desktop', 'wide'];
  
  /** @static {string} GALLERY_SELECTOR - CSS selector for the Swiper container */
  static GALLERY_SELECTOR = '.product-gallery';

  /**
   * Creates an instance of ProductGallery.
   * Initializes the Swiper instance and state tracking variables.
   */
  constructor() {
    super();
    /** @private {Swiper|null} Reference to the Swiper instance */
    this.swiper = null;
    /** @private {PhotoSwipeLightbox|null} Reference to the PhotoSwipe Lightbox instance */
    this.lightbox = null;
    /** @private {boolean} Flag to track initialization state */
    this.isInitialized = false;
    /** @private {HTMLElement|null} "1 / N" counter rendered over the carousel */
    this.counter = null;
    /** @private {HTMLElement[]} Thumbnail buttons rendered under the carousel */
    this.thumbs = [];
    /** @private {boolean} Whether thumbnail click handlers have been attached */
    this.thumbsBound = false;
  }

  /**
   * Lifecycle callback when the element is added to the DOM.
   * Ensures Swiper is only initialized after the page and required resources are loaded.
   * @override
   */
  connectedCallback() {
    // Wait for both window load and Swiper to be available
    if (document.readyState === 'complete') {
      this.waitForSwiper();
    } else {
      window.addEventListener('load', () => this.waitForSwiper());
    }
  }

  /**
   * Lifecycle callback when the element is removed from the DOM.
   * Cleans up event listeners and Swiper instance.
   * @override
   */
  disconnectedCallback() {
    this.destroySwiper();
    this.destroyPhotoSwipe();
    window.removeEventListener('screen-breakpoint', this.handleBreakpoint);
    clearInterval(this.swiperCheckInterval);
  }

  /**
   * Waits for the Swiper library to be available before initialization.
   * Implements a polling mechanism with a timeout.
   * @private
   */
  waitForSwiper() {
    // Check if Swiper is available
    if (typeof Swiper !== 'undefined') {
      this.init();
    } else {
      // Poll for Swiper availability
      this.swiperCheckInterval = setInterval(() => {
        if (typeof Swiper !== 'undefined') {
          clearInterval(this.swiperCheckInterval);
          this.init();
        }
      }, 100);

      // Timeout after 5 seconds
      setTimeout(() => {
        if (!this.isInitialized) {
          clearInterval(this.swiperCheckInterval);
          console.error('Swiper failed to load within timeout period');
        }
      }, 5000);
    }
  }

  /**
   * Initializes the component by setting up event listeners and initial state.
   * @private
   */
  init() {
    if (this.isInitialized) return;
    
    this.handleBreakpoint = this.handleBreakpoint.bind(this);
    this.counter = this.querySelector('[data-gallery-counter]');
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.bindThumbs();
    this.handleBreakpoint();
    window.addEventListener('screen-breakpoint', this.handleBreakpoint);
    this.initPhotoSwipe();
    this.isInitialized = true;
  }

  /**
   * Getter for Swiper configuration options.
   * @returns {Object} Configuration object for Swiper initialization
   * @private
   */
  get swiperConfig() {
    return {
      base: {
        slidesPerView: 1,
        mousewheelForceToAxis: true
      },
      breakpoints: {
        640: { slidesPerView: 1.6 },
        768: { slidesPerView: 2.5 },
        1024: { slidesPerView: 2 }
      }
    };
  }

  /**
   * Handles screen breakpoint changes and manages Swiper instance accordingly.
   * @param {CustomEvent} [event] - Screen breakpoint change event
   * @private
   */
  handleBreakpoint(event) {
    const currentMode = event?.detail?.mode || window.screenMode;
    const shouldDestroySwiper = ProductGallery.BREAKPOINT_MODES.includes(currentMode);

    if (shouldDestroySwiper) {
      this.destroySwiper();
    } else {
      this.initSwiper();
    }
  }

  /**
   * Initializes the Swiper instance if it doesn't exist.
   * @private
   */
  initSwiper() {
    if (!this.swiper) {
      const swiperElement = this.querySelector(ProductGallery.GALLERY_SELECTOR);
      if (swiperElement) {
        const config = {
          ...this.swiperConfig.base,
          breakpoints: this.swiperConfig.breakpoints,
          on: {
            slideChange: (sw) => {
              this.updateIndicators(sw.activeIndex);
              const prev = sw.slides[sw.previousIndex];
              if (!prev) return;
              prev.querySelectorAll('deferred-media').forEach((el) => {
                if (typeof el.pause === 'function') el.pause();
              });
            }
          }
        };

        // Only pass `navigation` when arrows are requested. An explicit
        // `navigation: undefined` overrides Swiper's module defaults and makes
        // the navigation module throw during init, which aborts the rest of the
        // initialisation (this is what previously hid the mobile pagination).
        if (this.hasAttribute('data-navigation')) {
          config.navigation = {
            prevEl: this.getNavButton(swiperElement, 'prev'),
            nextEl: this.getNavButton(swiperElement, 'next')
          };
        }

        // The optional pagination container is only used when arrows/dots are
        // wanted alongside the thumbnails; resolve it inside this gallery so a
        // `.swiper-pagination` from another slider on the page is never picked up.
        const paginationEl = swiperElement.querySelector('.swiper-pagination');
        if (paginationEl) {
          config.pagination = { el: paginationEl, clickable: true };
        }

        this.swiper = new Swiper(swiperElement, config);
        this.updateIndicators(this.swiper.activeIndex);
      }
    }
  }

  /**
   * Attaches click handlers to the thumbnail buttons (once). Tapping a
   * thumbnail slides the carousel to that media item.
   * @private
   */
  bindThumbs() {
    if (this.thumbsBound || this.thumbs.length === 0) return;
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        if (this.swiper) this.swiper.slideTo(index);
      });
    });
    this.thumbsBound = true;
  }

  /**
   * Reflects the active slide in the "1 / N" counter and the thumbnail strip.
   * @param {number} index - Zero-based index of the active slide
   * @private
   */
  updateIndicators(index) {
    if (this.counter) {
      const total = this.counter.dataset.total || this.thumbs.length;
      this.counter.textContent = `${index + 1} / ${total}`;
    }
    this.thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  /**
   * Returns the navigation arrow for the given direction, creating it inside
   * the swiper container on first use. Arrows only apply while the mobile
   * carousel is active, so they stay hidden on the laptop+ grid layout.
   * @param {HTMLElement} swiperElement - The .swiper container
   * @param {string} direction - Either 'prev' or 'next'
   * @returns {HTMLElement} The navigation button element
   * @private
   */
  getNavButton(swiperElement, direction) {
    let button = swiperElement.querySelector(`.swiper-button-${direction}`);
    if (!button) {
      button = document.createElement('div');
      button.className = `swiper-button-${direction} laptop:hidden`;
      swiperElement.appendChild(button);
    }
    return button;
  }

  /**
   * Destroys the Swiper instance and cleans up references.
   * @private
   */
  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Initializes PhotoSwipe lightbox for image zoom functionality.
   * Dynamically imports PhotoSwipe modules from assets.
   * @private
   */
  async initPhotoSwipe() {
    if (this.lightbox) return;

    // Wait for asset paths to be available
    if (!window.photoswipeAssetPath) {
      console.warn('PhotoSwipe asset paths not available');
      return;
    }

    try {
      // Dynamically import PhotoSwipe Lightbox
      const PhotoSwipeLightboxModule = await import(window.photoswipeAssetPath.lightbox);
      const PhotoSwipeLightbox = PhotoSwipeLightboxModule.default;

      // Initialize lightbox
      this.lightbox = new PhotoSwipeLightbox({
        gallery: this.querySelector(ProductGallery.GALLERY_SELECTOR) || ProductGallery.GALLERY_SELECTOR,
        children: 'a',
        pswpModule: () => import(window.photoswipeAssetPath.core).then(module => module.default),

        // Jewellery is shot on white: an opaque white stage reads as "the same
        // photo, bigger" instead of a dark translucent overlay with the page
        // showing through. Colours live in product-gallery.css (--pswp-* vars).
        bgOpacity: 1,

        // Zoom levels are relative to the image's natural size (1 = 100%).
        // Open at "fit", tap to jump to 2.5x of fit (≈ natural size on a phone
        // for a 1000px photo), pinch/scroll up to 4x of fit. Both are capped so a
        // small source image is never blown up past 1.5x natural, where it turns
        // to mush, and floored so tapping always visibly zooms.
        initialZoomLevel: 'fit',
        secondaryZoomLevel: (z) => Math.max(Math.min(z.fit * 2.5, 1.5), z.fit * 1.5),
        maxZoomLevel: (z) => Math.max(Math.min(z.fit * 4, 1.5), z.fit * 2),

        // One tap on the photo zooms in / out (the default toggles the toolbar,
        // which on a phone looks like nothing happened). Double-tap and pinch
        // still zoom; the X button, a vertical swipe or pinching in closes.
        imageClickAction: 'zoom',
        tapAction: 'zoom',
        doubleTapAction: 'zoom',
        wheelToZoom: true,

        // On a white stage the edge of a white-background photo is invisible,
        // so on phones a tap just outside it zooms like a tap on it would;
        // on desktop clicking the backdrop closes, as people expect.
        bgClickAction: function (point) {
          if (window.innerWidth < 1024) {
            if (this.currSlide) this.currSlide.toggleZoom(point);
          } else {
            this.close();
          }
        },

        // Keep the photo clear of the toolbar at the top and the counter.
        paddingFn: (viewportSize) => ({
          top: 56,
          bottom: 56,
          left: viewportSize.x < 1024 ? 0 : 40,
          right: viewportSize.x < 1024 ? 0 : 40
        })
      });

      // Keep the carousel on the photo the shopper was looking at in the
      // lightbox, so closing it never lands them on a different image. Videos
      // aren't lightbox children, so map the anchor back to its slide index
      // rather than trusting pswp.currIndex.
      this.lightbox.on('change', () => {
        const pswp = this.lightbox && this.lightbox.pswp;
        const anchor = pswp && pswp.currSlide && pswp.currSlide.data && pswp.currSlide.data.element;
        if (!anchor || !this.swiper) return;
        const slide = anchor.closest('.swiper-slide');
        const slides = Array.from(this.swiper.slides || []);
        const index = slides.indexOf(slide);
        if (index >= 0 && index !== this.swiper.activeIndex) {
          this.swiper.slideTo(index, 0);
        }
      });

      this.lightbox.init();
    } catch (error) {
      console.error('Failed to initialize PhotoSwipe:', error);
    }
  }

  /**
   * Destroys the PhotoSwipe lightbox instance and cleans up references.
   * @private
   */
  destroyPhotoSwipe() {
    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
  }
}

// Register the web component
customElements.define('product-gallery', ProductGallery); 